import { forwardRef, type FC, type ReactNode } from 'react';
import { css, cx } from '@linaria/atomic';
import { isFunction } from 'lodash-es';
import type { CommonHTMLButtonAttributes } from '@frontend/types';
import { useThemeValues } from '../../hooks';
import { Button } from '../button';
import * as styles from './text-button.styles';

const sparkIconStyle = css`
  align-items: center;
  display: flex;

  svg {
    margin-right: 8px;
  }
`;

type Props = CommonHTMLButtonAttributes & {
  children: ReactNode;
  disabled?: boolean;
  icon?: FC<React.PropsWithChildren<unknown>>;
  trackingId?: string;
  variant?: 'rounded' | 'rounded-small';
} & { color?: string };

/** @deprecated Instead, please use `<Button variant='secondary'>` from `@frontend/design-system` */
export const TextButton = forwardRef<HTMLButtonElement, Props>(
  ({ children, icon, trackingId, variant, className, ...rest }, ref) => {
    const hasIcon = isFunction(icon);
    const IconComponent = icon as FC<React.PropsWithChildren<unknown>>;
    const theme = useThemeValues();

    return theme.name === 'spark' ? (
      <Button
        className={cx(hasIcon ? sparkIconStyle : undefined, className)}
        destructive={rest?.color === 'critical' ? true : false}
        variant='tertiary'
        type='button'
        data-fabric-ds-name='TextButton'
        {...rest}
        ref={ref}
        data-trackingid={trackingId}
      >
        {!!icon && <IconComponent />}
        {children}
      </Button>
    ) : (
      <button
        className={cx(
          styles.textButtonBaseStyle,
          hasIcon && styles.hasIconStyle,
          variant && styles.textButtonVariantStyles[variant],
          className
        )}
        type='button'
        {...rest}
        ref={ref}
        data-trackingid={trackingId}
      >
        {!!icon && <IconComponent />}
        {children}
      </button>
    );
  }
);
TextButton.displayName = 'TextButton';
